import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  CssBaseline,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  Slide,
  Button,
  Stack,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  Container,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input/input";
import { Link } from "react-router-dom";
import UserProfileContext from "../../context/userProfile/UserProfileContext";
import UserDataAPI from "../../api/firebase/UserDataAPI";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RadioButtonUncheckedSharpIcon from "@mui/icons-material/RadioButtonUncheckedSharp";
import RadioButtonCheckedSharpIcon from "@mui/icons-material/RadioButtonCheckedSharp";
import AlertDialogSlide from "../../components/common/Dialog/AlertDialogSlide";
import { states } from "./IndianStates";

const SlideTransition = React.forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const initialFormData = {
  name: "",
  mobile: "",
  address: "",
  city: "",
  state: "",
  landmark: "",
  alternateMobile: "",
  pincode: "",
  isDefault: false,
  addressType: "Home",
};

const UserAddressesPage = ({
  selectAddress = false,
  selectedAddress = {},
  setSelectedAddress = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userProfileContext = useContext(UserProfileContext);
  const userDataApi = new UserDataAPI();

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [tempAddress, setTempAddress] = useState(null);

  const [open, setOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openAnchorEl = Boolean(anchorEl);

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});
  const [formBannerError, setFormBannerError] = useState("");

  useEffect(() => {
    if (userProfileContext.state !== null) {
      setUser(userProfileContext.state);
      if (Array.isArray(userProfileContext.state.addresses)) {
        setAddresses(userProfileContext.state.addresses);
      }
    }
  }, [userProfileContext.state]);

  const getStateDisplayName = (stateVal) => {
    if (!stateVal) return "";
    const found = states.find(
      (s) =>
        s.code?.toUpperCase() === stateVal?.toUpperCase() ||
        s.name?.toUpperCase() === stateVal?.toUpperCase()
    );
    return found ? found.name : stateVal;
  };

  const handleOpen = (editAddr = null) => {
    if (editAddr !== null) {
      setTempAddress(editAddr);
      setFormData({
        name: editAddr.name || "",
        mobile: editAddr.mobile || "",
        address: editAddr.address || "",
        city: editAddr.city || "",
        state: editAddr.state || "",
        landmark: editAddr.landmark || "",
        alternateMobile: editAddr.alternateMobile || "",
        pincode: editAddr.pincode || "",
        isDefault: Boolean(editAddr.isDefault),
        addressType: editAddr.addressType || "Home",
      });
    } else {
      setTempAddress(null);
      setFormData({
        name: user?.name || user?.displayName || "",
        mobile: user?.mobile || "",
        address: "",
        city: "",
        state: "",
        landmark: "",
        alternateMobile: "",
        pincode: "",
        isDefault: addresses.length === 0,
        addressType: "Home",
      });
    }
    setFormErrors({});
    setFormBannerError("");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setTempAddress(null);
    setFormErrors({});
    setFormBannerError("");
  };

  const handleClickAnchorEl = ({ index, event }) => {
    setAnchorEl(event.currentTarget);
    const targetAddr = addresses[index];
    const t_address = {
      index: index,
      isDefault: Boolean(targetAddr.isDefault),
      name: targetAddr.name || "",
      mobile: targetAddr.mobile || "",
      address: targetAddr.address || "",
      city: targetAddr.city || "",
      state: targetAddr.state || "",
      landmark: targetAddr.landmark || "",
      alternateMobile: targetAddr.alternateMobile || "",
      pincode: targetAddr.pincode || "",
      addressType: targetAddr.addressType || "Home",
    };
    setTempAddress(t_address);
  };

  const handleCloseAnchorEl = () => {
    setAnchorEl(null);
  };

  const handleEditAddress = (action = "EDIT") => {
    handleCloseAnchorEl();
    if (action === "EDIT") {
      handleOpen(tempAddress);
    } else {
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleAgreeDialog = () => {
    setOpenDialog(false);
    if (!tempAddress || tempAddress.index === undefined) return;
    setLoading(true);

    const prevAddresses = [...addresses];
    prevAddresses.splice(tempAddress.index, 1);
    const data = {
      payload: {
        addresses: [...prevAddresses],
      },
      id: user?.id,
    };
    userDataApi
      .addUserData(data)
      .then((res) => {
        if (res.success) {
          userProfileContext.setState((prev) => ({
            ...prev,
            ...data.payload,
          }));
        }
        setLoading(false);
        setTempAddress(null);
      })
      .catch((err) => {
        setLoading(false);
        console.error("Error deleting address:", err);
      });
  };

  const handleMakeDefault = ({ index }) => {
    setLoading(true);
    const prevAddresses = addresses.map((addr, i) => ({
      ...addr,
      isDefault: i === index,
    }));
    setAddresses(prevAddresses);
    const data = {
      payload: {
        addresses: [...prevAddresses],
      },
      id: user?.id,
    };
    userDataApi
      .addUserData(data)
      .then((res) => {
        if (res.success) {
          userProfileContext.setState((prev) => ({
            ...prev,
            ...data.payload,
          }));
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error("Error setting default address:", err);
      });
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (formBannerError) {
      setFormBannerError("");
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = "Please enter full name";
    }
    const cleanMobile = formData.mobile ? formData.mobile.replace(/\D/g, "") : "";
    if (!formData.mobile || cleanMobile.length === 0) {
      newErrors.mobile = "Mobile number is required";
    } else if (
      !isValidPhoneNumber(formData.mobile) &&
      !/^[6-9]\d{9}$/.test(cleanMobile.slice(-10))
    ) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }
    if (!formData.address || formData.address.trim().length < 5) {
      newErrors.address = "Please enter street address, flat/house number";
    }
    if (!formData.city || formData.city.trim().length < 2) {
      newErrors.city = "Please enter city or district";
    }
    if (!formData.state || formData.state.trim().length === 0) {
      newErrors.state = "Please select state";
    }
    if (!formData.pincode || !/^\d{6}$/.test(formData.pincode.trim())) {
      newErrors.pincode = "Please enter a valid 6-digit pincode";
    }
    if (
      formData.alternateMobile &&
      formData.alternateMobile.trim().length > 0 &&
      formData.alternateMobile !== "+91"
    ) {
      const cleanAlt = formData.alternateMobile.replace(/\D/g, "");
      if (
        !isValidPhoneNumber(formData.alternateMobile) &&
        !/^[6-9]\d{9}$/.test(cleanAlt.slice(-10))
      ) {
        newErrors.alternateMobile = "Please enter a valid alternate phone";
      }
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      setFormBannerError("Please fill all required fields correctly.");
      return;
    }
    setFormBannerError("");
    setLoading(true);

    const newAddress = {
      name: formData.name.trim(),
      mobile: formData.mobile.trim(),
      address: formData.address.trim(),
      city: formData.city.trim(),
      state: formData.state,
      landmark: formData.landmark ? formData.landmark.trim() : "",
      alternateMobile:
        formData.alternateMobile && formData.alternateMobile !== "+91"
          ? formData.alternateMobile.trim()
          : "",
      pincode: formData.pincode.trim(),
      isDefault: Boolean(formData.isDefault),
      addressType: formData.addressType || "Home",
    };

    const prevAddresses = Array.isArray(userProfileContext?.state?.addresses)
      ? [...userProfileContext.state.addresses]
      : [];

    const isAdd = tempAddress === null || tempAddress.index === undefined;
    const targetIndex = !isAdd ? tempAddress.index : prevAddresses.length;

    // If newAddress is marked default or is the very first address, unset others
    if (newAddress.isDefault || prevAddresses.length === 0) {
      newAddress.isDefault = true;
      prevAddresses.forEach((addr, i) => {
        if (!isAdd && i === targetIndex) return;
        addr.isDefault = false;
      });
    }

    if (isAdd) {
      prevAddresses.push({ ...newAddress });
    } else {
      prevAddresses[targetIndex] = {
        ...newAddress,
      };
    }

    const data = {
      payload: {
        addresses: [...prevAddresses],
      },
      id: user?.id,
    };

    userDataApi
      .addUserData(data)
      .then((res) => {
        if (res.success) {
          if (selectAddress) {
            setSelectedAddress({ ...newAddress });
          }
          userProfileContext.setState((prev) => ({
            ...prev,
            ...data.payload,
          }));
        }
        setLoading(false);
        handleClose();
      })
      .catch((err) => {
        setLoading(false);
        setFormBannerError("Failed to save address. Please try again.");
        console.error("Error saving address:", err);
      });
  };

  const getAddressTypeIcon = (type) => {
    switch (type) {
      case "Work":
        return <BusinessRoundedIcon sx={{ fontSize: 16 }} />;
      case "Other":
        return <LocationOnRoundedIcon sx={{ fontSize: 16 }} />;
      default:
        return <HomeRoundedIcon sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <Container component="main" maxWidth={selectAddress ? "sm" : "lg"} sx={{ py: selectAddress ? 0 : { xs: 3, md: 5 }, px: selectAddress ? 0 : 2 }}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 10 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CssBaseline />

      {/* Standalone Page Header */}
      {!selectAddress && (
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Box sx={{ mb: 1.5 }}>
            <Link
              to="/userProfile"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                color: "#64748b",
                textDecoration: "none",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to My Account
            </Link>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    letterSpacing: "-0.02em",
                    fontSize: { xs: "1.65rem", sm: "2rem" },
                  }}
                >
                  Saved Addresses
                </Typography>
                <Chip
                  label={`${addresses.length} ${addresses.length === 1 ? "address" : "addresses"}`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(47, 93, 126, 0.1)",
                    color: "primary.main",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  }}
                />
              </Stack>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Manage your delivery addresses and set your default shipping destination
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => handleOpen(null)}
              sx={{
                borderRadius: "12px",
                py: 1.2,
                px: 2.5,
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.92rem",
                boxShadow: "0 4px 14px rgba(47, 93, 126, 0.25)",
                whiteSpace: "nowrap",
              }}
            >
              Add New Address
            </Button>
          </Box>
        </Box>
      )}

      {/* Empty State when no addresses exist */}
      {addresses.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            borderRadius: "20px",
            border: "1px dashed #cbd5e1",
            textAlign: "center",
            bgcolor: "#ffffff",
            my: 2,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "rgba(47, 93, 126, 0.08)",
              color: "primary.main",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <AddLocationAltOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}>
            No Saved Addresses Found
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", maxWidth: 400, mx: "auto", mb: 3 }}
          >
            Add your shipping address to enjoy faster checkout with automated address pre-selection.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => handleOpen(null)}
            sx={{
              borderRadius: "10px",
              py: 1.2,
              px: 3,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Add Your First Address
          </Button>
        </Paper>
      )}

      {/* Addresses Grid */}
      {addresses.length > 0 && (
        <Grid container spacing={selectAddress ? 1.75 : 2.5}>
          {addresses.map((address, _index) => {
            const isSelected =
              selectedAddress === address ||
              (selectedAddress?.id && selectedAddress?.id === address?.id) ||
              (selectedAddress?.address === address?.address &&
                selectedAddress?.pincode === address?.pincode &&
                selectedAddress?.name === address?.name);

            const typeLabel = address.addressType || "Home";

            return (
              <Grid
                item
                xs={12}
                sm={selectAddress ? 12 : 6}
                key={_index}
              >
                <Paper
                  onClick={() => {
                    if (selectAddress) setSelectedAddress(address);
                  }}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    borderRadius: "16px",
                    border: selectAddress
                      ? isSelected
                        ? "2px solid"
                        : "1px solid"
                      : address.isDefault
                      ? "2px solid #2f5d7e"
                      : "1px solid #e2e8f0",
                    borderColor: selectAddress
                      ? isSelected
                        ? "primary.main"
                        : "#e2e8f0"
                      : address.isDefault
                      ? "primary.main"
                      : "#e2e8f0",
                    bgcolor: selectAddress
                      ? isSelected
                        ? "rgba(47, 93, 126, 0.04)"
                        : "#ffffff"
                      : address.isDefault
                      ? "rgba(47, 93, 126, 0.02)"
                      : "#ffffff",
                    boxShadow:
                      (selectAddress && isSelected) || (!selectAddress && address.isDefault)
                        ? "0 8px 24px rgba(47, 93, 126, 0.12)"
                        : "0 2px 8px rgba(0,0,0,0.03)",
                    cursor: selectAddress ? "pointer" : "default",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <Box>
                    {/* Top Row: Type Badge, Default Chip, Radio or Action Buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                        {selectAddress && (
                          <IconButton
                            color="primary"
                            onClick={() => setSelectedAddress(address)}
                            sx={{ p: 0.25, mr: 0.5 }}
                          >
                            {isSelected ? (
                              <RadioButtonCheckedSharpIcon sx={{ fontSize: 22 }} />
                            ) : (
                              <RadioButtonUncheckedSharpIcon
                                sx={{ fontSize: 22, color: "#94a3b8" }}
                              />
                            )}
                          </IconButton>
                        )}

                        <Chip
                          icon={getAddressTypeIcon(typeLabel)}
                          label={typeLabel.toUpperCase()}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            bgcolor: "rgba(47, 93, 126, 0.08)",
                            color: "primary.main",
                            borderRadius: "6px",
                            "& .MuiChip-icon": { color: "primary.main" },
                          }}
                        />

                        {address.isDefault && (
                          <Chip
                            icon={<CheckRoundedIcon sx={{ fontSize: "14px !important", color: "#16a34a !important" }} />}
                            label="DEFAULT"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              bgcolor: "#f0fdf4",
                              color: "#15803d",
                              borderRadius: "6px",
                              border: "1px solid #bbf7d0",
                            }}
                          />
                        )}
                      </Stack>

                      {/* Right Actions: Standalone Direct Edit/Delete OR 3-dots in Checkout */}
                      {!selectAddress ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title="Edit Address">
                            <IconButton
                              size="small"
                              onClick={() => handleOpen({ ...address, index: _index })}
                              sx={{
                                color: "#64748b",
                                "&:hover": { color: "primary.main", bgcolor: "rgba(47, 93, 126, 0.08)" },
                              }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Address">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTempAddress({ ...address, index: _index });
                                setOpenDialog(true);
                              }}
                              sx={{
                                color: "#94a3b8",
                                "&:hover": { color: "#dc2626", bgcolor: "#fee2e2" },
                              }}
                            >
                              <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Tooltip title="Address Options">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClickAnchorEl({ index: _index, event: e });
                            }}
                            size="small"
                          >
                            <MoreVertIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    {/* Recipient Name */}
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1rem", mb: 0.75 }}
                    >
                      {address.name}
                    </Typography>

                    {/* Address Text */}
                    <Typography
                      variant="body2"
                      sx={{ color: "#334155", lineHeight: 1.5, mb: 1 }}
                    >
                      {address.address}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.85rem", mb: 1 }}
                    >
                      {address.city}, {getStateDisplayName(address.state)} - {address.pincode}
                    </Typography>

                    {address.landmark && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "inline-block",
                          color: "#475569",
                          bgcolor: "#f1f5f9",
                          px: 1,
                          py: 0.25,
                          borderRadius: "4px",
                          fontSize: "0.72rem",
                          mb: 1.5,
                        }}
                      >
                        Landmark: {address.landmark}
                      </Typography>
                    )}

                    {/* Contact Number */}
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.85rem" }}
                      >
                        Phone: {address.mobile}
                        {address.alternateMobile && ` • Alt: ${address.alternateMobile}`}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Card Bottom Footer: Set Default Button */}
                  {!selectAddress && (
                    <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f1f5f9" }}>
                      {!address.isDefault ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleMakeDefault({ index: _index })}
                          sx={{
                            color: "primary.main",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            textTransform: "none",
                            p: 0,
                            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                          }}
                        >
                          Set as Default Address
                        </Button>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ color: "#166534", fontWeight: 700, fontSize: "0.75rem" }}
                        >
                          ✓ Default delivery destination
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}

          {/* Checkout mode: Add New Address button at bottom */}
          {selectAddress && (
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AddOutlinedIcon />}
                onClick={() => handleOpen(null)}
                sx={{
                  py: 1.5,
                  borderRadius: "14px",
                  borderStyle: "dashed",
                  borderWidth: "2px",
                  borderColor: "#94a3b8",
                  color: "primary.main",
                  fontWeight: 700,
                  bgcolor: "#f8fafc",
                  textTransform: "none",
                  fontSize: "0.95rem",
                  "&:hover": {
                    borderStyle: "dashed",
                    borderWidth: "2px",
                    borderColor: "primary.main",
                    bgcolor: "rgba(47, 93, 126, 0.05)",
                  },
                }}
              >
                Add a new Address
              </Button>
            </Grid>
          )}
        </Grid>
      )}

      {/* Address Options Dropdown Menu (for checkout mode) */}
      <Menu
        anchorEl={anchorEl}
        open={openAnchorEl}
        onClose={handleCloseAnchorEl}
        PaperProps={{
          elevation: 2,
          sx: {
            borderRadius: "10px",
            minWidth: 120,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <MenuItem dense onClick={() => handleEditAddress("EDIT")}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Edit
          </Typography>
        </MenuItem>
        <MenuItem dense onClick={() => handleEditAddress("DELETE")}>
          <Typography variant="body2" sx={{ color: "#dc2626", fontWeight: 600 }}>
            Remove
          </Typography>
        </MenuItem>
      </Menu>

      {/* Add / Edit Address Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        TransitionComponent={SlideTransition}
        aria-labelledby="address-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "20px" },
            maxHeight: { xs: "100%", sm: "90vh" },
            boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#ffffff",
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(15, 23, 42, 0.6)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            pb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e2e8f0",
            bgcolor: "#ffffff",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "rgba(47, 93, 126, 0.1)",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AddLocationAltOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                id="address-dialog-title"
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: "#0f172a",
                  fontSize: { xs: "1.05rem", sm: "1.18rem" },
                  lineHeight: 1.2,
                }}
              >
                {tempAddress !== null ? "Edit Delivery Address" : "Add Delivery Address"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                Ensure safe and accurate delivery of your orders
              </Typography>
            </Box>
          </Stack>

          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              color: "#64748b",
              bgcolor: "#f8fafc",
              border: "1px solid #f1f5f9",
              "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" },
            }}
            aria-label="Close address dialog"
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Scrollable Form Content */}
        <DialogContent
          sx={{
            p: { xs: 2, sm: 3 },
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {formBannerError && (
            <Alert severity="error" sx={{ borderRadius: "10px", py: 0.5 }}>
              {formBannerError}
            </Alert>
          )}

          {/* Section 1: Contact Details */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#475569",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                display: "block",
                mb: 1.5,
              }}
            >
              1. Contact Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  error={Boolean(formErrors.name)}
                  helperText={formErrors.name}
                  placeholder="e.g. Rahul Sharma"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineRoundedIcon
                          sx={{ fontSize: 20, color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <PhoneInput
                    placeholder="10-digit Mobile Number *"
                    defaultCountry="IN"
                    value={formData.mobile}
                    onChange={(val) => handleFieldChange("mobile", val || "")}
                    className={`modern-phone-input ${
                      formErrors.mobile ? "has-error" : ""
                    }`}
                    maxLength={15}
                  />
                  {formErrors.mobile && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#d32f2f",
                        fontSize: "0.72rem",
                        mt: 0.5,
                        display: "block",
                        ml: 1,
                      }}
                    >
                      {formErrors.mobile}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ borderColor: "#f1f5f9" }} />

          {/* Section 2: Address Details */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#475569",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                display: "block",
                mb: 1.5,
              }}
            >
              2. Delivery Address
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    handleFieldChange("pincode", val);
                  }}
                  error={Boolean(formErrors.pincode)}
                  helperText={formErrors.pincode}
                  placeholder="6-digit PIN code"
                  size="small"
                  InputProps={{
                    sx: { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="City / District"
                  name="city"
                  value={formData.city}
                  onChange={(e) => handleFieldChange("city", e.target.value)}
                  error={Boolean(formErrors.city)}
                  helperText={formErrors.city}
                  placeholder="e.g. Mumbai"
                  size="small"
                  InputProps={{
                    sx: { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={2}
                  label="Flat, House no., Building, Apartment, Street"
                  name="address"
                  value={formData.address}
                  onChange={(e) => handleFieldChange("address", e.target.value)}
                  error={Boolean(formErrors.address)}
                  helperText={formErrors.address}
                  placeholder="e.g. Flat 402, Green Valley Apartments, MG Road"
                  size="small"
                  InputProps={{
                    sx: { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  required
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={(e) => handleFieldChange("state", e.target.value)}
                  error={Boolean(formErrors.state)}
                  helperText={formErrors.state}
                  size="small"
                  InputProps={{
                    sx: { borderRadius: "10px" },
                  }}
                >
                  {states.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Landmark (Optional)"
                  name="landmark"
                  value={formData.landmark}
                  onChange={(e) => handleFieldChange("landmark", e.target.value)}
                  placeholder="e.g. Near Apollo Pharmacy"
                  size="small"
                  InputProps={{
                    sx: { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <PhoneInput
                    placeholder="Alternate Phone (Optional)"
                    defaultCountry="IN"
                    value={formData.alternateMobile}
                    onChange={(val) => handleFieldChange("alternateMobile", val || "")}
                    className={`modern-phone-input ${
                      formErrors.alternateMobile ? "has-error" : ""
                    }`}
                    maxLength={20}
                  />
                  {formErrors.alternateMobile && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#d32f2f",
                        fontSize: "0.72rem",
                        mt: 0.5,
                        display: "block",
                        ml: 1,
                      }}
                    >
                      {formErrors.alternateMobile}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ borderColor: "#f1f5f9" }} />

          {/* Section 3: Address Type & Preferences */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#475569",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                display: "block",
                mb: 1.5,
              }}
            >
              3. Address Type & Preferences
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
              {[
                { type: "Home", icon: <HomeRoundedIcon sx={{ fontSize: 18 }} /> },
                { type: "Work", icon: <BusinessRoundedIcon sx={{ fontSize: 18 }} /> },
                { type: "Other", icon: <LocationOnRoundedIcon sx={{ fontSize: 18 }} /> },
              ].map((item) => {
                const isActive = formData.addressType === item.type;
                return (
                  <Chip
                    key={item.type}
                    icon={item.icon}
                    label={item.type}
                    clickable
                    onClick={() => handleFieldChange("addressType", item.type)}
                    sx={{
                      py: 2,
                      px: 1,
                      borderRadius: "10px",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      bgcolor: isActive ? "rgba(47, 93, 126, 0.12)" : "#f8fafc",
                      color: isActive ? "primary.main" : "#64748b",
                      border: "1px solid",
                      borderColor: isActive ? "primary.main" : "#e2e8f0",
                      "& .MuiChip-icon": {
                        color: isActive ? "primary.main" : "#64748b",
                      },
                      "&:hover": {
                        bgcolor: isActive ? "rgba(47, 93, 126, 0.18)" : "#f1f5f9",
                      },
                    }}
                  />
                );
              })}
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isDefault}
                  onChange={(e) => handleFieldChange("isDefault", e.target.checked)}
                  color="primary"
                  sx={{ "& .MuiSvgIcon-root": { borderRadius: "4px" } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
                  Make this my default delivery address
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            p: 2,
            px: { xs: 2, sm: 3 },
            borderTop: "1px solid #e2e8f0",
            bgcolor: "#ffffff",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              borderColor: "#cbd5e1",
              color: "#475569",
              py: 1,
              px: 2.5,
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CheckRoundedIcon />
              )
            }
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 800,
              py: 1.1,
              px: 3,
              boxShadow: "0 4px 14px rgba(47, 93, 126, 0.3)",
            }}
          >
            {loading
              ? "Saving..."
              : tempAddress !== null
              ? "Update Address"
              : "Save Address"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Address Confirmation Dialog */}
      <AlertDialogSlide
        handleOnAgree={handleAgreeDialog}
        handleOnClose={handleCloseDialog}
        isOpen={openDialog}
        title="Remove Address"
        description={"Are you sure you want to remove this address?"}
      />
    </Container>
  );
};

export default UserAddressesPage;
